import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormListFieldData } from 'antd';

import EPTable from '../EPTable';
import { WrapperContainer, TableContainer, ScrollContainer, CustomSpin } from './EPTableWrapper.styles';
import { CustomScrollbar } from './CustomScrollbar/CustomScrollbar';

interface ScrollContainerWrapperProps {
  useCustomScroll: boolean;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  scrollRef: MutableRefObject<HTMLDivElement>;
  children: React.ReactNode;
}

const ScrollContainerWrapper: React.FC<ScrollContainerWrapperProps> = ({
  useCustomScroll,
  onScroll,
  scrollRef,
  children,
}) => {
  return useCustomScroll ? (
    <CustomScrollbar onScroll={onScroll} scrollRef={scrollRef}>
      <div style={{ height: '100%', width: '100%' }}>{children}</div>
    </CustomScrollbar>
  ) : (
    <ScrollContainer ref={scrollRef} onScroll={onScroll}>
      {children}
    </ScrollContainer>
  );
};

interface EPTableWrapperProps {
  sticky?: boolean;
  dataSource?: FormListFieldData[];
  initialRowCount?: number;
  rowIncrementCount?: number;
  expandable?: Record<string, unknown>;
  scroll?: Record<string, unknown>;
  onScrollEnd?: () => void;
  tableLayout?: 'auto' | 'fixed';
  viewportHeightMultiplier?: number;
  showAllRowsButton?: boolean;
  showScrollButtons?: boolean;
  loader?: React.ReactNode;
  isLoading?: boolean;
  useCustomScroll?: boolean;
  onChange?: (pagination: unknown, filters: unknown, sorter: unknown) => void;
  [key: string]: unknown;
}

const EPTableWrapper: React.FC<EPTableWrapperProps> = ({
  sticky = false,
  dataSource = [],
  initialRowCount = 10,
  rowIncrementCount = 10,
  expandable = {},
  scroll = {},
  onScrollEnd,
  tableLayout = 'auto',
  viewportHeightMultiplier = 0.6,
  loader,
  isLoading = false,
  useCustomScroll = false,
  onChange,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [visibleData, setVisibleData] = useState(() => dataSource.slice(0, initialRowCount));
  const [allLocalDataLoaded, setAllLocalDataLoaded] = useState(false);
  const [showAllRows] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Update visible data whenever the data source changes
  useEffect(() => {
    if (showAllRows) {
      setVisibleData(dataSource);
      setAllLocalDataLoaded(true);
    } else {
      setVisibleData(dataSource.slice(0, initialRowCount));
      setAllLocalDataLoaded(dataSource.length <= initialRowCount);
    }
    setLoadingMore(false);
  }, [dataSource, initialRowCount, showAllRows]);

  // Setup ResizeObserver to adapt to container size changes
  useEffect(() => {
    if (!containerRef.current || !tableContainerRef.current) return;

    const observeResize = () => {
      if (tableContainerRef.current && containerRef.current.parentElement) {
        const parentHeight = containerRef.current.parentElement.clientHeight;

        const targetHeight = viewportHeightMultiplier
          ? Math.floor(window.innerHeight * viewportHeightMultiplier)
          : parentHeight;

        tableContainerRef.current.style.height = `${targetHeight}px`;
      }
    };

    observeResize();

    const resizeObserver = new ResizeObserver(observeResize);

    resizeObserver.observe(containerRef.current);
    if (containerRef.current.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    window.addEventListener('resize', observeResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', observeResize);
    };
  }, [viewportHeightMultiplier]);

  const handleScroll = () => {
    if (showAllRows || loadingMore || isLoading) return;

    const container = tableContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 20;

    if (isBottom) {
      if (visibleData.length < dataSource.length) {
        // Store scroll position before updating
        const currentScrollTop = scrollTop;

        setLoadingMore(true);
        setTimeout(() => {
          const nextLength = Math.min(visibleData.length + rowIncrementCount, dataSource.length);
          setVisibleData(dataSource.slice(0, nextLength));

          if (nextLength === dataSource.length) {
            setAllLocalDataLoaded(true);
          }
          setLoadingMore(false);

          // Restore scroll position after state update
          requestAnimationFrame(() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollTop = currentScrollTop;
            }
          });
        }, 150);
      } else if (allLocalDataLoaded && onScrollEnd && !loadingMore) {
        // Store scroll position before external data load
        const currentScrollTop = scrollTop;

        setLoadingMore(true);
        Promise.resolve(onScrollEnd()).finally(() => {
          setLoadingMore(false);
          // Restore scroll position after external data load
          requestAnimationFrame(() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollTop = currentScrollTop;
            }
          });
        });
      }
    }
  };
  const loaderElement = loader || (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <CustomSpin />
    </div>
  );

  return (
    <WrapperContainer>
      <TableContainer ref={containerRef}>
        <ScrollContainerWrapper useCustomScroll={useCustomScroll} onScroll={handleScroll} scrollRef={tableContainerRef}>
          <EPTable
            {...props}
            sticky={sticky}
            dataSource={visibleData}
            tableLayout={tableLayout}
            expandable={expandable}
            pagination={false}
            scroll={{ x: scroll.x }}
            onChange={onChange}
          />
          {(loadingMore || isLoading) && loaderElement}
        </ScrollContainerWrapper>
      </TableContainer>
    </WrapperContainer>
  );
};

export default EPTableWrapper;
